// src/components/ui.jsx
// Small shared UI kit so pages stay clean. All theme-aware via tb-* tokens.

import React from "react";
import { X, ChevronDown } from "./icons";

// ---- Card ---------------------------------------------------------------
export const Card = ({ className = "", children }) => (
  <div className={`surface rounded-2xl border border-tb-border dark:border-tb-d-border min-w-0 ${className}`}>
    {children}
  </div>
);

// ---- Button -------------------------------------------------------------
const BTN = {
  primary: "bg-tb-cta text-white hover:brightness-105 shadow-green-glow",
  ghost: "border border-tb-border dark:border-tb-d-border text-tb-muted hover:text-tb-ink dark:hover:text-tb-d-text",
  danger: "bg-red-600 text-white hover:brightness-110",
  subtle: "bg-tb-bg-2 dark:bg-tb-d-inset text-tb-ink dark:text-tb-d-text hover:brightness-95",
};

export const Button = ({ variant = "primary", className = "", children, disabled, ...rest }) => (
  <button
    disabled={disabled}
    className={`inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold
                transition active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none
                ${BTN[variant]} ${className}`}
    {...rest}
  >
    {children}
  </button>
);

// ---- Input / Select / Textarea -----------------------------------------
const FIELD =
  "w-full rounded-xl border border-tb-border dark:border-tb-d-border bg-white dark:bg-tb-d-inset px-3 py-2 text-sm " +
  "text-tb-ink dark:text-tb-d-text outline-none focus:border-tb-brand focus:ring-1 focus:ring-tb-brand placeholder:text-tb-muted/60";

export const Input = ({ className = "", ...rest }) => (
  <input className={`${FIELD} ${className}`} {...rest} />
);

// Custom dropdown so the menu matches the brand (native <select> popups can't
// be styled). Accepts the same value/onChange shape as a select, with options
// as <option> children OR an `options` array of {value,label}.
export const Select = ({ value, onChange, children, options, disabled, className = "" }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  // derive option list from children or the options prop
  const opts = options
    ? options
    : React.Children.toArray(children)
        .filter(Boolean)
        .map((c) => ({ value: c.props.value, label: c.props.children }));

  const current = opts.find((o) => String(o.value) === String(value));

  React.useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (v) => {
    setOpen(false);
    if (onChange) onChange({ target: { value: v } });
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`${FIELD} flex items-center justify-between text-left ${disabled ? "opacity-50 pointer-events-none" : ""}`}
      >
        <span className="truncate">{current ? current.label : ""}</span>
        <ChevronDown size={16} className={`shrink-0 ml-2 text-tb-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1.5 min-w-full w-max max-w-[280px] rounded-xl border border-tb-border dark:border-tb-d-border surface shadow-lg p-1.5 max-h-64 overflow-auto">
          {opts.map((o) => {
            const active = String(o.value) === String(value);
            return (
              <button
                key={String(o.value)}
                type="button"
                onClick={() => pick(o.value)}
                className={`block w-full text-left px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                  active
                    ? "bg-tb-brand/10 text-tb-brand font-medium"
                    : "hover:bg-tb-bg-2 dark:hover:bg-tb-d-inset"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="block text-sm font-medium mb-1.5">{label}</span>
    {children}
    {hint && <span className="block mt-1 text-xs text-tb-muted">{hint}</span>}
  </label>
);

// ---- Badge --------------------------------------------------------------
const BADGE = {
  green: "bg-tb-brand/10 text-tb-brand",
  gray: "bg-tb-bg-2 dark:bg-tb-d-inset text-tb-muted",
  amber: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  red: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400",
  blue: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400",
};

export const Badge = ({ tone = "gray", className = "", children }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${BADGE[tone]} ${className}`}>
    {children}
  </span>
);

// ---- Spinner ------------------------------------------------------------
export const Spinner = ({ className = "" }) => (
  <div className={`h-5 w-5 rounded-full border-2 border-tb-brand border-t-transparent animate-spin ${className}`} />
);

export const Loading = ({ label = "Loading" }) => (
  <div className="flex items-center gap-3 text-sm text-tb-muted py-12 justify-center">
    <Spinner /> {label}
  </div>
);

// ---- EmptyState ---------------------------------------------------------
export const EmptyState = ({ title, hint }) => (
  <div className="text-center py-16">
    <p className="font-display font-semibold">{title}</p>
    {hint && <p className="mt-1 text-sm text-tb-muted">{hint}</p>}
  </div>
);

// ---- Modal --------------------------------------------------------------
export const Modal = ({ open, onClose, title, children, width = "max-w-lg", bodyClassName = "p-5" }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full min-w-0 ${width} surface rounded-2xl border border-tb-border dark:border-tb-d-border shadow-xl my-8`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-tb-border dark:border-tb-d-border">
          <h2 className="font-display font-bold">{title}</h2>
          <button onClick={onClose} className="text-tb-muted hover:text-tb-ink dark:hover:text-tb-d-text" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className={bodyClassName}>{children}</div>
      </div>
    </div>
  );
};